import { forwardRef, ReactNode, Ref } from 'react';
import styled, { css } from 'styled-components';

interface GridBaseProps {
	children: ReactNode | ReactNode[];
}

interface GridLayout {
	base?: string;
	S?: string;
	M?: string;
	L?: string;
}

interface GridLayouts {
	base?: string[];
	S?: string[];
	M?: string[];
	L?: string[];
}

type GridPropsLayout = {
	areas: GridLayouts;
	columns?: GridLayouts;
	rows?: GridLayouts;
	gap?: string | GridLayout;
} & GridBaseProps;

type GridPropsNoLayout = {
	areas: string[];
	columns?: string[];
	rows?: string[];
	gap?: string;
} & GridBaseProps;

export type GridProps = GridPropsLayout | GridPropsNoLayout;
type BreakPointChunk = string | null;
type BreakPoints = [base: BreakPointChunk, S: BreakPointChunk, M: BreakPointChunk, L: BreakPointChunk];

const elements = {
	container: styled.div<{ $breakPoints: BreakPoints }>`
		display: grid;

		${({ $breakPoints }) => {
			const base = $breakPoints[0];
			if (base != null) {
				return base;
			}

			return null;
		}}

		${({ $breakPoints }) => {
			const S = $breakPoints[1];
			if (S != null) {
				return css`
					@media only screen and (min-width: 600px) {
						${S}
					}
				`;
			}

			return null;
		}}

		${({ $breakPoints }) => {
			const M = $breakPoints[2];
			if (M != null) {
				return css`
					@media only screen and (min-width: 905px) {
						${M}
					}
				`;
			}

			return null;
		}}

		${({ $breakPoints }) => {
			const L = $breakPoints[3];
			if (L != null) {
				return css`
					@media only screen and (min-width: 1440px) {
						${L}
					}
				`;
			}

			return null;
		}}
	`,
};

function CellulaGrid({ children, areas, columns, rows, gap }: GridProps, ref: Ref<HTMLDivElement>): JSX.Element {
	const breakpoints: BreakPoints = [null, null, null, null];
	if (Array.isArray(areas)) {
		// Todo: fix type overlaps so this is not needed
		columns = columns as string[] | undefined;
		rows = rows as string[] | undefined;
		gap = gap as string | undefined;

		breakpoints[0] = retrieveGridStyle({
			areas: areas,
			columns: columns,
			rows: rows,
			gap: gap,
		});
	} else {
		// Todo: fix type overlaps so this is not needed
		areas = areas as GridLayouts;
		columns = columns as GridLayouts | undefined;
		rows = rows as GridLayouts | undefined;
		gap = gap as GridLayout | string | undefined;

		if (areas.base != null) {
			breakpoints[0] = retrieveGridStyle({
				areas: areas.base,
				columns: columns?.base,
				rows: rows?.base,
				gap: typeof gap === 'string' ? gap : gap?.base,
			});
		}

		if (areas.S != null) {
			breakpoints[2] = retrieveGridStyle({
				areas: areas.S,
				columns: columns?.S,
				rows: rows?.S,
				gap: typeof gap === 'string' ? gap : gap?.S,
			});
		}

		if (areas.M != null) {
			breakpoints[2] = retrieveGridStyle({
				areas: areas.M,
				columns: columns?.M,
				rows: rows?.M,
				gap: typeof gap === 'string' ? gap : gap?.M,
			});
		}

		if (areas.L != null) {
			breakpoints[3] = retrieveGridStyle({
				areas: areas.L,
				columns: columns?.L,
				rows: rows?.L,
				gap: typeof gap === 'string' ? gap : gap?.L,
			});
		}
	}

	// todo: find a way to hide child.

	return (
		<elements.container ref={ref} $breakPoints={breakpoints}>
			{children}
		</elements.container>
	);
}

interface GridSpec {
	areas: string[];
	columns?: string[];
	rows?: string[];
	gap?: string;
}

interface GridStyle {
	gridTemplateAreas?: string;
	gridTemplateColumns?: string;
	gridTemplateRows?: string;
	gap?: string;
}

function retrieveGridStyle(spec: GridSpec): string {
	const builder: Partial<GridStyle> = {
		gridTemplateAreas: spec.areas.map((part) => `"${part}"`).join('\n'),
	};

	if (spec.gap) {
		builder.gap = spec.gap;
	}

	if (spec.columns) {
		builder.gridTemplateColumns = spec.columns.join(' ');
	}

	if (spec.rows) {
		builder.gridTemplateRows = spec.rows.join(' ');
	}

	const collapsed = `
		${builder.gridTemplateAreas ? `grid-template-areas: ${builder.gridTemplateAreas};` : ''}
		${builder.gridTemplateColumns ? `grid-template-columns: ${builder.gridTemplateColumns};` : ''}
		${builder.gridTemplateRows ? `grid-template-rows: ${builder.gridTemplateRows};` : ''}
		${builder.gap ? `gap: ${builder.gap};` : ''}
	`;

	return collapsed;
}

/* ########################################################################## */
/* ############################### PUBLIC API ############################### */
/* ########################################################################## */

/**
 * A component to provide a grid system to structure the application UI.
 */
const Grid = forwardRef(CellulaGrid);

export type { GridBaseProps, GridLayout, GridLayouts };
export { Grid };
